import camelCaseKeys from "camelcase-keys";
import { fromPairs, pickBy } from "lodash-es";
import { useContext } from "react";
import { QueryKey, useQuery } from "react-query";

import { FeatureFlagsContext } from "../contexts/feature-flags/FeatureFlagsContext";
import { IFeatureFlag } from "../generated/types";
import { NO_AUTH_HEADERS_INIT } from "../services/auth";
import { ICountry, IPurposeCode } from "../types/country";
import { fetchWithAuth, ResponseError } from "./fetchWithAuth";

export namespace CountriesKey {
    export const all: QueryKey = ["countries"];
    export const paymentRules = [...all, "paymentRules"];
    export const euCountries = [...all, "euCountries"];
}

export interface IPaymentRulesResponse {
    country_data: {
        [k in string]: {
            latitude: number | null;
            longitude: number | null;
            account_number_format: string | null;
            account_number_name: string | null;
            address_needed: boolean | null;
            currency: string | null;
            info: string | null;
            is_iban: boolean | null;
            max_payment: string | null;
            name: string;
            payment_rail_name: string | null;
            routing_number_1_cc_name: string | null;
            routing_number_1_format: string | null;
            routing_number_1_name: string | null;
            routing_number_2_cc_name: string | null;
            routing_number_2_format: string | null;
            routing_number_2_name: string | null;
            swift_needed_for_local: boolean | null;
            time: number | null;
            type: string;
            swift_days: number | null;
            local_days: number | null;
            swift_cutoff: string | null;
            local_cutoff: string | null;
            fx_cutoff: string | null;
            tax_id_needed_for_company: boolean | null;
            tax_id_name_for_company: string | null;
            tax_id_format_for_company: string | null;
            tax_id_needed_for_individual: boolean | null;
            tax_id_name_for_individual: string | null;
            tax_id_format_for_individual: string | null;
            paypal: {
                fee: number | null;
                limit: number | null;
            } | null;
            venmo: {
                fee: number | null;
                limit: number | null;
            } | null;
        };
    };
    eu_countries: { [k in string]: { name: string; latitude: number; longitude: number } };
    purpose_codes: {
        [k: string]: IPurposeCode[];
    };
}

export interface IPassportCountry {
    countryCode: string;
    name: string;
}

export interface ICountriesMap {
    countryByCountryCode: { [countryCode: string]: ICountry };
    countryCodeByCurrency: { [currency: string]: string };
    passportCountries: IPassportCountry[];
}

export function useCountriesQuery() {
    const { isFeatureFlagEnabled } = useContext(FeatureFlagsContext);
    return useQuery<ICountriesMap, ResponseError>(CountriesKey.paymentRules, () =>
        // auth not required. this endpoint is used for recipient self-onboarding.
        fetchWithAuth("payments/rules", "json", { headersInit: NO_AUTH_HEADERS_INIT }).then((response) => {
            const parsedResponse = parseCountries(response);
            if (!isFeatureFlagEnabled(IFeatureFlag.EnableGbpLocalPayments)) {
                parsedResponse.countryByCountryCode.GB.paymentRules.type = "usd_only";
            }
            return parsedResponse;
        }),
    );
}

/**
 * The network request is almost identical to the above, but we cannot share a cache.
 * This is because the above does some parsing within the React Query call, which is not great.
 */
export function useEuCountries() {
    return useQuery<string[], ResponseError>(CountriesKey.euCountries, async () => {
        const { eu_countries }: IPaymentRulesResponse = await fetchWithAuth("payments/rules", "json", {
            headersInit: NO_AUTH_HEADERS_INIT,
        });
        return Object.keys(eu_countries);
    });
}

export function parseCountries(response: IPaymentRulesResponse): ICountriesMap {
    const countryMap = response.country_data;
    const euCountry = response.country_data.EU;
    if (euCountry) {
        Object.entries(response.eu_countries).forEach(([k, v]) => {
            countryMap[k] = { ...euCountry, ...v };
        });
        delete countryMap.EU;
    }

    const passportCountries: IPassportCountry[] = Object.entries(countryMap).map(([countryCode, { name }]) => ({
        name,
        countryCode,
    }));

    const purposeCodesByCountry = response.purpose_codes;

    const countries = Object.fromEntries(
        Object.entries(countryMap)
            .map(
                ([k, v]) =>
                    [
                        k,
                        ((country_code, { name, currency, latitude, longitude, ...rules }): ICountry => ({
                            countryCode: country_code,
                            name,
                            currency,
                            latitude,
                            longitude,
                            paymentRules: {
                                ...camelCaseKeys(rules), // TODO(abeer): Set default value for swift_days - 0
                                swiftNeededForLocal: rules.swift_needed_for_local ?? false,
                                addressNeeded: rules.address_needed ?? false,
                                taxIdNeededForCompany: rules.tax_id_needed_for_company ?? false,
                                taxIdNeededForIndividual: rules.tax_id_needed_for_individual ?? false,
                                isIban: rules.is_iban ?? false,
                                isEurozone: country_code in response.eu_countries,
                                routingNumber_1CcName: rules.routing_number_1_cc_name,
                                routingNumber_1Format: rules.routing_number_1_format,
                                routingNumber_1Name: rules.routing_number_1_name,
                                routingNumber_2CcName: rules.routing_number_2_cc_name,
                                routingNumber_2Format: rules.routing_number_2_format,
                                routingNumber_2Name: rules.routing_number_2_name,
                            },
                            purposeCodes: purposeCodesByCountry[country_code],
                        }))(k, v),
                    ] as const,
            )
            .sort((a, b) => {
                const countryA = a[1];
                const countryB = b[1];

                // Put the USA at the top of the list
                if (countryA.countryCode === "US") {
                    return -1;
                } else if (countryB.countryCode === "US") {
                    return 1;
                } else {
                    return countryA.name.toLocaleLowerCase().localeCompare(countryB.name.toLocaleLowerCase());
                }
            }),
    );

    const filteredCountries = pickBy(countries, (c) => c.paymentRules.type !== "not_supported");
    const countryCodeByCurrency = fromPairs(Object.values(filteredCountries).map((c) => [c.currency, c.countryCode]));
    // MXN payments aren't supported so it doesn't appear in the payment rules but we still support it for conversions.
    countryCodeByCurrency.MXN = "MX";

    return {
        countryByCountryCode: filteredCountries,
        countryCodeByCurrency,
        passportCountries,
    };
}
